import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, History, Loader2, Clock, CalendarClock, UserCog, FileCheck2 } from "lucide-react";

const TYPES = {
  time_clock: { label: "Time Clock", icon: Clock, cls: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
  payroll_hours: { label: "Payroll Hours", icon: CalendarClock, cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" },
  pay_setup: { label: "Pay Setup", icon: UserCog, cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" },
  register: { label: "Payroll Register", icon: FileCheck2, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
};
const fmtAt = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const fmtDay = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "—");

function TypeBadge({ type }) {
  const t = TYPES[type] || { label: type, icon: History, cls: "bg-muted text-muted-foreground" };
  const Icon = t.icon;
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${t.cls}`}><Icon className="h-3 w-3" />{t.label}</span>;
}

function ChangeRow({ r }) {
  return (
    <tr className="border-t border-border align-top" data-testid={`cl-row-${r.id}`}>
      <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">{fmtAt(r.at)}</td>
      <td className="px-3 py-2 text-xs">{r.by}</td>
      <td className="px-3 py-2"><TypeBadge type={r.type} /></td>
      <td className="px-3 py-2 font-medium">{r.employee_name || <span className="text-muted-foreground">—</span>}</td>
      <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDay(r.date)}</td>
      <td className="px-3 py-2 text-xs font-medium">{r.field}</td>
      <td className="px-3 py-2 text-xs">
        {(r.before || r.after) ? (
          <span className="inline-flex flex-wrap items-center gap-1 tabular-nums">
            <span className="rounded-sm bg-red-50 px-1 text-red-700 line-through decoration-red-400/60 dark:bg-red-950/30 dark:text-red-400">{r.before || "—"}</span>
            <span className="text-muted-foreground">→</span>
            <span className="rounded-sm bg-emerald-50 px-1 font-semibold text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">{r.after || "—"}</span>
          </span>
        ) : null}
        {r.detail && <div className="mt-0.5 text-muted-foreground">{r.detail}</div>}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{r.source}</td>
    </tr>
  );
}

export const PayrollChangeLog = ({ propertyId, start: pStart, end: pEnd, propertyName }) => {
  const [start, setStart] = useState(pStart || "");
  const [end, setEnd] = useState(pEnd || "");
  const [type, setType] = useState("all");
  const [emp, setEmp] = useState("all");
  const [items, setItems] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { setStart(pStart || ""); setEnd(pEnd || ""); }, [pStart, pEnd]);

  const load = useCallback(() => {
    if (!propertyId || !start || !end) return;
    setItems(null);
    api.get("/payroll-sheet/change-log", { params: { property_id: propertyId, start, end, type: type === "all" ? undefined : type } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load change log"); setItems([]); });
  }, [propertyId, start, end, type]);
  useEffect(() => { load(); }, [load]);

  const employees = useMemo(() => {
    const m = {};
    (items || []).forEach((r) => { if (r.employee_id) m[r.employee_id] = r.employee_name; });
    return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  const rows = (items || []).filter((r) => emp === "all" || r.employee_id === emp);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/payroll-sheet/change-log/export", {
        params: { property_id: propertyId, start, end, type: type === "all" ? undefined : type, employee_id: emp === "all" ? undefined : emp },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(propertyName || "Property").replace(/\s+/g, "_")}_Payroll_Changes_${start}_to_${end}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="payroll-change-log">
      <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
        <div><Label className="label-caps text-[0.6rem] text-muted-foreground">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="cl-start" /></div>
        <div><Label className="label-caps text-[0.6rem] text-muted-foreground">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="cl-end" /></div>
        <div>
          <Label className="label-caps text-[0.6rem] text-muted-foreground">Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" data-testid="cl-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All changes</SelectItem>
              {Object.entries(TYPES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-caps text-[0.6rem] text-muted-foreground">Employee</Label>
          <Select value={emp} onValueChange={setEmp}>
            <SelectTrigger className="w-52" data-testid="cl-employee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="cl-count">{items ? `${rows.length} change${rows.length === 1 ? "" : "s"}` : ""}</span>
          <Button variant="outline" onClick={exportXlsx} disabled={exporting || !items} data-testid="cl-export-btn">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Excel
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Payroll hours & register changes are tracked by work date / pay period; pay setup changes by the date they were made. Saved-hours tracking started Oct 6, 2026 — Time Clock edits include earlier history.</p>
      {items === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center border border-dashed border-border text-muted-foreground" data-testid="cl-empty">
          <History className="h-8 w-8" /><p className="mt-3 text-sm">No changes recorded for this period.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm" data-testid="cl-table">
            <thead>
              <tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Changed At</th><th className="px-3 py-2">By</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Work Date</th><th className="px-3 py-2">What</th>
                <th className="px-3 py-2">Before → After</th><th className="px-3 py-2">Changed From</th>
              </tr>
            </thead>
            <tbody>{rows.map((r) => <ChangeRow key={r.id} r={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
